import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isSameDay } from 'date-fns';
import { createEmptyChargeItem } from '~/components/BillModal/utils/lineItem.utils';
import { toDatestampString } from '~/models/time';
import { AD_HOC_PAYMENT_DEFAULTS, netTermsToOption } from '../AdHocBillModal.types';
import { useAdHocScheduledData } from './useAdHocScheduledData';
import { useInvoiceDraftDates } from './useInvoiceDraftDates';
import { useScheduledInvoiceDraft } from './useScheduledInvoiceDraft';
const isOccupiedDate = (date, invoiceDraftDates)=>{
    var _ref;
    return (_ref = invoiceDraftDates === null || invoiceDraftDates === void 0 ? void 0 : invoiceDraftDates.dates().some((d)=>isSameDay(date, d))) !== null && _ref !== void 0 ? _ref : false;
};
const scheduledDateParam = (issueDate, invoiceDraftDates)=>{
    if (!isOccupiedDate(issueDate, invoiceDraftDates)) return null;
    return toDatestampString(issueDate);
};
const toAttachmentFiles = (attachments)=>attachments.map((att)=>_object_spread_props(_object_spread({}, att), {
            loading: false
        }));
export const useAdHocDateSync = (param)=>{
    let { relationshipId, form, defaultEmailSubject, defaultEmailContent, setCurrentAttachments, setRestoredAttachments } = param;
    var _ref;
    const { invoiceDraftDates } = useInvoiceDraftDates(relationshipId, {
        refreshOnHookMount: true
    });
    const issueDate = form.watch('issueDate');
    const setValue = form.setValue;
    const dateParam = useMemo(()=>scheduledDateParam(issueDate, invoiceDraftDates), [
        issueDate,
        invoiceDraftDates
    ]);
    const isDateOccupied = dateParam != null;
    const { scheduledData, isLoading: isScheduledDataLoading } = useAdHocScheduledData(relationshipId, dateParam);
    const draftDate = dateParam != null ? issueDate : null;
    const { draft: scheduledDraft, isLoading: isDraftLoading } = useScheduledInvoiceDraft(relationshipId, draftDate, {
        refreshOnHookMount: true
    });
    const appliedForDateRef = useRef(null);
    useEffect(()=>{
        const draftReady = dateParam == null || !isDraftLoading;
        if (dateParam != null && scheduledData != null && draftReady && appliedForDateRef.current !== dateParam) {
            var _ref;
            appliedForDateRef.current = dateParam;
            setValue('coverCreditCardFees', scheduledData.coverCreditCardFees);
            setValue('netTerms', netTermsToOption(scheduledData.netTerms));
            if (scheduledData.legalTermsDocument != null) {
                setValue('includeTermsOfService', true);
                setValue('termsOfServiceId', scheduledData.legalTermsDocument.legalTermsId);
            } else {
                setValue('includeTermsOfService', false);
                setValue('termsOfServiceId', '');
            }
            if (scheduledData.issuedEmail != null) {
                setValue('emailSubject', scheduledData.issuedEmail.subject);
                setValue('emailContent', scheduledData.issuedEmail.content);
            } else {
                setValue('emailSubject', defaultEmailSubject);
                setValue('emailContent', defaultEmailContent);
            }
            const restoredAttachmentsFromDraft = (_ref = scheduledDraft === null || scheduledDraft === void 0 ? void 0 : scheduledDraft.attachments) !== null && _ref !== void 0 ? _ref : [];
            setCurrentAttachments(toAttachmentFiles(restoredAttachmentsFromDraft));
            setRestoredAttachments({
                ids: restoredAttachmentsFromDraft.map((att)=>att.id),
                issueDate
            });
        } else if (dateParam == null && appliedForDateRef.current != null) {
            appliedForDateRef.current = null;
            setValue('coverCreditCardFees', AD_HOC_PAYMENT_DEFAULTS.coverCreditCardFees);
            setValue('netTerms', AD_HOC_PAYMENT_DEFAULTS.netTerms);
            setValue('includeTermsOfService', AD_HOC_PAYMENT_DEFAULTS.includeTermsOfService);
            setValue('termsOfServiceId', AD_HOC_PAYMENT_DEFAULTS.termsOfServiceId);
            setValue('emailSubject', defaultEmailSubject);
            setValue('emailContent', defaultEmailContent);
            setCurrentAttachments([]);
            setRestoredAttachments({
                ids: [],
                issueDate: null
            });
            if (form.getValues('items').length === 0) {
                form.items.replace([
                    createEmptyChargeItem()
                ]);
            }
        }
    }, [
        dateParam,
        issueDate,
        scheduledData,
        scheduledDraft,
        isDraftLoading,
        setValue,
        defaultEmailSubject,
        defaultEmailContent,
        setCurrentAttachments,
        setRestoredAttachments,
        form
    ]);
    const highlightDay = useCallback((day)=>isOccupiedDate(day, invoiceDraftDates), [
        invoiceDraftDates
    ]);
    const occupiedDates = useMemo(()=>{
        var _ref;
        return (_ref = invoiceDraftDates === null || invoiceDraftDates === void 0 ? void 0 : invoiceDraftDates.dates()) !== null && _ref !== void 0 ? _ref : [];
    }, [
        invoiceDraftDates
    ]);
    return {
        highlightDay,
        occupiedDates,
        isDateOccupied,
        isScheduledDataLoading: isScheduledDataLoading || isDraftLoading,
        restoredLineItems: dateParam != null ? (_ref = scheduledDraft === null || scheduledDraft === void 0 ? void 0 : scheduledDraft.lineItems) !== null && _ref !== void 0 ? _ref : [] : []
    };
};
